import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import { Check } from 'lucide-react';
import Skeleton from '../../components/ui/Skeleton';

const OFFLINE_API = 'https://api3.cultaz.com/api/ol_app/certificate-types';
const INSTALLER_URL = 'https://cultaz.com/Cultaz-setup.exe';

const Offline = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    fetch(OFFLINE_API)
      .then((r) => r.json())
      .then((data) => {
        if (!mounted) return;
        // defensive mapping - backend may return array or object
        const list = Array.isArray(data) ? data : data?.data || data?.types || [];
        const mapped = list.map((t) => ({
          id: t.id || t.type_code || t.name,
          name: t.name || t.type_code || 'Plan',
          description: t.description || t.note || '',
          price: t.base_price || t.price || t.amount || 0,
          duration_days: t.duration_days || t.days || null,
          features: t.features || []
        }));
        setPlans(mapped);
      })
      .catch((e) => {
        console.error('Failed to fetch offline plans', e);
        setError('Unable to load plans');
      })
      .finally(() => mounted && setLoading(false));

    return () => { mounted = false; };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-6 lg:px-12 py-10">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold">Cultaz Offline - Desktop POS</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto mt-3">A lightweight offline desktop app optimized for in-store operations - process sales and manage inventory without internet. Choose a plan below to download the installer and get started.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="p-6 bg-card rounded-xl"> <h3 className="font-semibold">Designed for Stores</h3><p className="text-sm text-muted-foreground">Simple setup and fast checkout.</p></div>
          <div className="p-6 bg-card rounded-xl"> <h3 className="font-semibold">Reliable & Fast</h3><p className="text-sm text-muted-foreground">Works on modest hardware with predictable performance.</p></div>
          <div className="p-6 bg-card rounded-xl"> <h3 className="font-semibold">Flexible Licensing</h3><p className="text-sm text-muted-foreground">Monthly plans and volume discounts available.</p></div>
        </div>

        <div className="bg-card p-6 rounded-xl mb-8">
          <h2 className="font-semibold mb-2">What's included</h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {['Fast POS and receipt printing','Inventory management with barcode support','Local customer and sales history','Offline reports and exports','Manual or scheduled sync with cloud','Flexible license options & priority support'].map((t)=> (
              <li key={t} className="flex items-center gap-3"><Check className="w-4 h-4 text-green-500" /> <span className="text-sm text-muted-foreground">{t}</span></li>
            ))}
          </ul>
        </div>

        <section className="mb-8">
          <div className="container mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-6">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground mb-2">Choose a License</h2>
              <p className="text-muted-foreground">Select any license below to download the Cultaz Desktop installer. Pricing and features are loaded from our backend.</p>
            </div>

            {loading ? (
              <Skeleton count={3} />
            ) : error ? (
              <div className="text-destructive text-center py-6">{error}</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {plans.map((plan) => (
                  <div key={plan.id} className="border border-border bg-card rounded-2xl p-6 flex flex-col">
                    <div className="mb-4">
                      <h3 className="font-heading text-xl font-bold text-foreground">{plan.name}</h3>
                      {plan.description && <p className="text-sm text-muted-foreground mt-1">{plan.description}</p>}
                    </div>

                    <div className="flex-1">
                      <div className="text-3xl font-bold text-primary mb-2">{plan.price ? `₦${Number(plan.price).toLocaleString()}` : 'Contact'}</div>
                      {plan.duration_days && <div className="text-sm text-muted-foreground mb-3">Duration: {plan.duration_days} day(s)</div>}

                      {plan.features && plan.features.length > 0 && (
                        <ul className="space-y-2 mb-4">
                          {plan.features.map((f, i) => (
                            <li key={i} className="text-sm text-muted-foreground">• {typeof f === 'string' ? f : f.name || JSON.stringify(f)}</li>
                          ))}
                        </ul>
                      )}
                    </div>

                    <div className="mt-4">
                      <Link to="/downloads" className="block">
                        <button className="w-full px-4 py-3 bg-primary text-white rounded-lg font-medium">Download Installer</button>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Screenshot gallery */}
        <div className="container mx-auto px-6 lg:px-12 mt-4">
          <h2 className="font-semibold mb-4">Screenshots</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { src: '/desktop-app-screenshots/UI1.png', alt: 'Main POS Interface' },
              { src: '/desktop-app-screenshots/startup-page.png', alt: 'Startup Page' },
              { src: '/desktop-app-screenshots/settings-page.png', alt: 'Settings Page' },
              { src: '/desktop-app-screenshots/customer-facing-screen-detection.png', alt: 'Customer Screen Detection' },
              { src: '/desktop-app-screenshots/network-searching.png', alt: 'Network Search' },
              { src: '/desktop-app-screenshots/manual-client-connection-via-ip-address.png', alt: 'Manual Client Connection' }
            ].map((img, index) => (
              <div key={index} className="bg-card rounded-lg overflow-hidden">
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full h-56 object-cover"
                  onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/assets/images/site-placeholder.svg'; }}
                />
              </div>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Offline;
